import React, { useState, useEffect, useContext } from 'react';
import { ApiContext } from '../../App';
import { toast } from 'react-toastify';
import { getAllEmails } from '../../utils/emailHelpers';

const AvisManagement = ({ showDeleteDialog, setShowDeleteDialog, avisToDelete, setAvisToDelete }) => {
  const API = useContext(ApiContext);
  const [avis, setAvis] = useState([]);
  const [individuals, setIndividuals] = useState([]);
  const [formData, setFormData] = useState({
    individual: '',
    rating: 5,
    comment: '',
    isPublished: false
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAvis, setEditingAvis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [individualSearchTerm, setIndividualSearchTerm] = useState('');

  useEffect(() => {
    fetchAvis();
    fetchIndividuals();
  }, []);

  const fetchAvis = async () => {
    try {
      const response = await API.get('/api/avis');
      setAvis(response.data);
    } catch (error) {
      console.error('Error fetching avis:', error);
      toast.error('Erreur lors du chargement des avis');
    }
  };

  const fetchIndividuals = async () => {
    try {
      const response = await API.get('/api/avis/individuals');
      setIndividuals(response.data);
    } catch (error) {
      console.error('Error fetching individuals:', error);
      toast.error('Erreur lors du chargement des individuals');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingAvis) {
        await API.put(`/api/avis/${editingAvis._id}`, formData);
        toast.success('Avis mis à jour avec succès');
      } else {
        await API.post('/api/avis', formData);
        toast.success('Avis créé avec succès');
      }

      fetchAvis();
      setIsModalOpen(false);
      setEditingAvis(null);
      setIndividualSearchTerm('');
      setFormData({
        individual: '',
        rating: 5,
        comment: '',
        isPublished: false
      });
    } catch (error) {
      console.error('Error saving avis:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (avisItem) => {
    setEditingAvis(avisItem);
    setFormData({
      individual: avisItem.individual?._id || '',
      rating: avisItem.rating,
      comment: avisItem.comment,
      isPublished: avisItem.isPublished
    });
    setIsModalOpen(true);
  };

  // Filtrer les individuals selon le terme de recherche
  const filteredIndividuals = individualSearchTerm
    ? individuals.filter(individual =>
        individual.Name.toLowerCase().includes(individualSearchTerm.toLowerCase()) ||
        getAllEmails(individual.professionalEmail).some(email =>
          email.toLowerCase().includes(individualSearchTerm.toLowerCase())
        ) ||
        getAllEmails(individual.personalEmail).some(email =>
          email.toLowerCase().includes(individualSearchTerm.toLowerCase())
        ) ||
        individual.companyId?.name?.toLowerCase().includes(individualSearchTerm.toLowerCase())
      )
    : individuals;

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <i
        key={i}
        className={`bi bi-star${i < rating ? '-fill' : ''} text-yellow-400`}
      ></i>
    ));
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold text-gray-800">Liste des Avis</h3>
        <button
          onClick={() => {
            setEditingAvis(null);
            setIndividualSearchTerm('');
            setFormData({
              individual: '',
              rating: 5,
              comment: '',
              isPublished: false
            });
            setIsModalOpen(true);
          }}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          <i className="bi bi-plus mr-2"></i>
          Nouvel Avis
        </button>
      </div>

      {/* Liste des avis */}
      <div className="space-y-4">
        {avis.map((avisItem) => (
          <div key={avisItem._id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4">
                {/* Photo de profil */}
                <div className="flex-shrink-0">
                  {avisItem.individual?.photo ? (
                    <img
                      src={avisItem.individual.photo}
                      alt={avisItem.individual.Name}
                      className="h-12 w-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="h-12 w-12 bg-gray-300 rounded-full flex items-center justify-center">
                      <i className="bi bi-person text-gray-600 text-lg"></i>
                    </div>
                  )}
                </div>

                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <h4 className="font-semibold text-gray-900">
                      {avisItem.individual?.Name}
                    </h4>
                    <span className="text-sm text-gray-500">
                      {avisItem.individual?.companyId?.name || 'Entreprise non définie'}
                    </span>
                  </div>

                  <div className="flex items-center space-x-2 mb-2">
                    <div className="flex space-x-1">
                      {renderStars(avisItem.rating)}
                    </div>
                    <span className="text-sm text-gray-600">({avisItem.rating}/5)</span>
                    {avisItem.isPublished && (
                      <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                        Publié
                      </span>
                    )}
                  </div>

                  <p className="text-gray-700 text-sm">{avisItem.comment}</p>

                  <div className="mt-2 text-xs text-gray-500">
                    Créé le {new Date(avisItem.createdAt).toLocaleDateString('fr-FR')}
                  </div>
                </div>
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={() => handleEdit(avisItem)}
                  className="text-indigo-600 hover:text-indigo-800 text-sm"
                >
                  <i className="bi bi-pencil"></i>
                </button>
                <button
                  onClick={() => {
                    setAvisToDelete(avisItem._id);
                    setShowDeleteDialog(true);
                  }}
                  className="text-red-600 hover:text-red-800 text-sm"
                >
                  <i className="bi bi-trash"></i>
                </button>
              </div>
            </div>
          </div>
        ))}

        {avis.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            Aucun avis trouvé. Créez votre premier avis !
          </div>
        )}
      </div>

      {/* Modal de création/édition */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {editingAvis ? 'Modifier l\'avis' : 'Nouvel avis'}
              </h3>
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setIndividualSearchTerm('');
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Individual
                </label>

                {/* Barre de recherche pour les individuals */}
                <div className="relative mb-3">
                  <input
                    type="text"
                    placeholder="Rechercher par nom, email ou entreprise..."
                    value={individualSearchTerm}
                    onChange={(e) => setIndividualSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i className="bi bi-search text-gray-400"></i>
                  </div>
                  {individualSearchTerm && (
                    <button
                      type="button"
                      onClick={() => setIndividualSearchTerm('')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center hover:scale-110 transition-transform"
                    >
                      <i className="bi bi-x-circle-fill text-gray-400 hover:text-gray-600"></i>
                    </button>
                  )}
                </div>

                <select
                  value={formData.individual}
                  onChange={(e) => setFormData({ ...formData, individual: e.target.value })}
                  required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  size="5"
                >
                  <option value="">Sélectionner un individual</option>
                  {filteredIndividuals.map((individual) => (
                    <option key={individual._id} value={individual._id}>
                      {individual.Name} - {individual.companyId?.name || 'Sans entreprise'}
                    </option>
                  ))}
                </select>

                {/* Message si aucun résultat */}
                {individualSearchTerm && filteredIndividuals.length === 0 && (
                  <p className="text-sm text-gray-500 mt-2 italic">
                    Aucun individual trouvé pour "{individualSearchTerm}"
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Note (1-5 étoiles)
                </label>
                <select
                  value={formData.rating}
                  onChange={(e) => setFormData({ ...formData, rating: parseInt(e.target.value) })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <option key={rating} value={rating}>
                      {rating} étoile{rating > 1 ? 's' : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Commentaire
                </label>
                <textarea
                  value={formData.comment}
                  onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                  rows={4}
                  required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Écrivez votre commentaire..."
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isPublished"
                  checked={formData.isPublished}
                  onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })}
                  className="mr-2"
                />
                <label htmlFor="isPublished" className="text-sm text-gray-700">
                  Publier cet avis
                </label>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-4 rounded-lg font-medium disabled:opacity-50"
                >
                  {loading ? 'Enregistrement...' : (editingAvis ? 'Modifier' : 'Créer')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false);
                    setIndividualSearchTerm('');
                  }}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 px-4 rounded-lg font-medium"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AvisManagement;
